import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format } from 'date-fns';
import {
  resolvePropertyLabel,
  resolveLabelWithParent,
  getPropertyDisplayNameWithParent,
  type PropertyDisplaySource,
} from '@/lib/domain/property-label';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const DRAFT_PROPERTY_NAME = 'Nieuwe property (concept)';

export type { PropertyDisplaySource };
export { resolvePropertyLabel, resolveLabelWithParent, getPropertyDisplayNameWithParent };

/** Legacy tuple API — delegates to {@link resolvePropertyLabel}. */
export function getPropertyDisplayName(
  name: string,
  contents?: { title?: string | null; language?: string }[] | null,
  channelAccommodationName?: string | null,
  channelRawData?: unknown,
): string {
  return resolvePropertyLabel({ name, contents, channelAccommodationName, channelRawData });
}

/**
 * Channel / Beds24 property listing name (max 50 in UI). Uses stored value when set, else property name.
 */
export function getEffectiveChannelAccommodationName(
  channelAccommodationName: string | null | undefined,
  name: string,
): string {
  const c = channelAccommodationName?.trim();
  if (c) return c.slice(0, 50);
  return name.slice(0, 50);
}

/**
 * Parse a UTC date as a calendar date, ignoring timezone offset.
 * Booking check-in/check-out dates are stored as midnight UTC and represent
 * calendar dates, not specific moments in time. Using `new Date(isoString)`
 * directly and formatting in local time shifts them to the previous day in
 * negative-UTC timezones (e.g. Curaçao UTC-4).
 *
 * Idempotent: a Date that is already a calendar date (local midnight, the output of this
 * function or of `parseISO('yyyy-MM-dd')`) is returned as-is. Before, re-parsing such a value
 * in a positive-UTC browser (Europe/Amsterdam: local midnight = 22:00/23:00 UTC the day
 * before) shifted it one day back — the booking detail page (client, Europe/Amsterdam) passed
 * `parseCalendarDate(checkIn)` to the statement engine, which parsed it again and produced an
 * extra unpaid contract month (1 Nov → 31 Oct → "2026-10"), €600 too much outstanding versus the
 * server (UTC), 25 sep 2026. On the server (UTC) local midnight equals UTC midnight, so this
 * branch changes nothing there.
 */
export function parseCalendarDate(date: Date | string): Date {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (typeof date !== 'string' && isLocalMidnight(d)) return new Date(d.getTime());
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function isLocalMidnight(d: Date): boolean {
  return d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0 && d.getMilliseconds() === 0;
}

/**
 * Canonical `yyyy-MM` month key for long-term rent rows — matches {@link parseCalendarDate}
 * + `format(..., 'yyyy-MM')` on the client. Never use raw `format(booking.checkIn, 'yyyy-MM')`
 * (server timezone would skew the first month vs the dashboard).
 */
export function bookingCalendarMonthKeyFromStoredDate(date: Date | string): string {
  return format(parseCalendarDate(date), 'yyyy-MM');
}

export function formatDate(date: Date | string): string {
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

export const STANDARD_CURRENCIES = new Set([
  'EUR', 'USD', 'GBP', 'CHF', 'JPY', 'CNY', 'AUD', 'CAD', 'SEK', 'NOK', 'DKK',
  'PLN', 'CZK', 'HUF', 'RON', 'BGN', 'HRK', 'ISK', 'TRY', 'RUB', 'BRL', 'MXN',
  'INR', 'KRW', 'THB', 'SGD', 'HKD', 'NZD', 'ZAR',
]);

/**
 * Legacy/alias currency codes that are collapsed onto one canonical code.
 * ANG (Netherlands Antillean guilder) was replaced by XCG (Caribbean guilder)
 * at par, so both are one and the same currency for every bucket, filter and
 * display in the app. New aliases go here and nowhere else.
 */
export const CURRENCY_CODE_ALIASES: Readonly<Record<string, string>> = { ANG: 'XCG', NAF: 'XCG', NAFL: 'XCG' };

/**
 * The single rule for turning any stored/typed currency string into its
 * canonical uppercase ISO code: trims, defaults to EUR and resolves aliases.
 * Every place that compares, buckets or formats by currency goes through here
 * (directly or via normalizeCurrencyCode in currency-amounts.ts).
 */
export function canonicalCurrencyCode(code: string | null | undefined): string {
  const upper = (code?.trim() || 'EUR').toUpperCase();
  return CURRENCY_CODE_ALIASES[upper] ?? upper;
}

/**
 * ISO code passed to Intl for display. Currencies without Intl support (XCG)
 * fall through the STANDARD_CURRENCIES check and render with an explicit code
 * prefix ("XCG 38,35") — guilder amounts must stay visually distinct from euros.
 */
export function displayCurrencyForFormat(isoCode: string | null | undefined): string {
  return canonicalCurrencyCode(isoCode);
}

export function formatCurrency(amount: number, currency = 'EUR'): string {
  const code = displayCurrencyForFormat(currency);
  if (!STANDARD_CURRENCIES.has(code)) {
    return `${code} ${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)}`;
  }
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(amount);
  } catch {
    return `${code} ${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)}`;
  }
}

export function formatCurrencyNL(amount: number, currency = 'EUR'): string {
  const code = displayCurrencyForFormat(currency);
  if (!STANDARD_CURRENCIES.has(code)) {
    return `${code} ${new Intl.NumberFormat('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)}`;
  }
  try {
    return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: code }).format(amount);
  } catch {
    return `${code} ${new Intl.NumberFormat('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)}`;
  }
}

export function formatCurrencyWholeNL(amount: number, currency = 'EUR'): string {
  const code = displayCurrencyForFormat(currency);
  const rounded = Math.round(amount);
  if (!STANDARD_CURRENCIES.has(code)) {
    return `${code} ${new Intl.NumberFormat('nl-NL', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(rounded)}`;
  }
  try {
    return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: code, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(rounded);
  } catch {
    return `${code} ${new Intl.NumberFormat('nl-NL', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(rounded)}`;
  }
}

export function formatDateNL(date: Date | string): string {
  return new Date(date).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function generateSlug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}
